import React, { useState } from 'react';
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native';
import moment from 'moment';
import GradientWrapper from './GradientWrapper';
import { ODSText } from '../../atoms/ODSText';
import { ODSIcon } from '../../atoms/ODSIcon';
import getScreenType from '../../../utils/getScreenType';
import { getTokens } from '../../../design-tokens/color-tokens';

const isNhaAgreement = (item: any) => {
    return (
        item?.draft_agreement_type?.toLowerCase() === 'nha' ||
        Boolean(item?.is_nha_generated) ||
        Boolean(item?.nha_contract_url) ||
        Boolean(item?.is_nha) ||
        item?.agreement_type?.toLowerCase() === 'nha'
    );
};

const getPropertyDisplayAddress = (item: any) => {
    const prop = item?.property;
    if (!prop) return 'PROPERTY ADDRESS';

    const parts: string[] = [];
    if (prop.address_line1) parts.push(prop.address_line1.trim());
    if (prop.address_line2 && !parts.includes(prop.address_line2.trim())) {
        parts.push(prop.address_line2.trim());
    }
    if (prop.city && !parts.includes(prop.city.trim())) {
        parts.push(prop.city.trim());
    }
    if (prop.postcode && !parts.includes(prop.postcode.trim())) {
        parts.push(prop.postcode.trim());
    }

    if (parts.length > 0) {
        return parts.join(', ').toUpperCase();
    }
    if (prop.display_address) {
        return String(prop.display_address).toUpperCase();
    }
    if (prop.property_name) {
        return String(prop.property_name).toUpperCase();
    }
    return 'PROPERTY ADDRESS';
};

const getNegotiatorName = (item: any) => {
    if (item?.agent?.user) {
        const first = item?.agent?.user?.first_name || '';
        const last = item?.agent?.user?.last_name || '';
        const full = `${first} ${last}`.trim();
        if (full) return full;
    }
    if (item?.agent?.first_name) {
        const first = item?.agent?.first_name || '';
        const last = item?.agent?.last_name || '';
        const full = `${first} ${last}`.trim();
        if (full) return full;
    }
    if (item?.negotiator_name) return item.negotiator_name;
    if (item?.branch?.name) return item.branch.name;
    return 'N/A';
};

const getStartDateFormatted = (item: any) => {
    const dateVal =
        item?.open_flow_rental_offer?.tenancy_start_date ||
        item?.start_date ||
        item?.createdAt ||
        item?.created_at;
    if (!dateVal) return 'N/A';
    return moment(dateVal).format('DD/MM/YYYY');
};

const getBranchName = (item: any) => {
    return item?.branch?.name || 'Openbrix';
};

const getAgreementTypeLabel = (item: any) => {
    const isNha = isNhaAgreement(item);
    if (isNha) return 'NHA Contract';
    const type = item?.draft_agreement_type ? String(item.draft_agreement_type).toUpperCase() : null;
    if (type === 'AST' || type === 'APT') return 'APT Contract';
    if (type) return `${type} Contract`;
    return 'APT Contract';
};

const getTenancyProgressText = (item: any) => {
    if (item?.is_completed || item?.percentage_completed === 100) {
        return '100% Completed';
    }
    if (item?.percentage_completed != null) {
        return `${item.percentage_completed}% Progress`;
    }
    return 'Active';
};

const getProcessedFlowData = (rawArray: any[]) => {
    if (!Array.isArray(rawArray)) return [];
    const processed: any[] = [];

    rawArray.forEach(item => {
        if (item?._card_key) {
            processed.push(item);
            return;
        }
        const hasMoveOut = Boolean(
            (item?.move_out_status && item?.move_out_status > 0) ||
            item?.open_flow_move_out ||
            item?.is_move_out
        );
        const isMoveInCompleted = Boolean(item?.is_completed || item?.percentage_completed === 100 || item?.move_in_status === 1);

        if (hasMoveOut && isMoveInCompleted) {
            // 1. Move In Card (Completed)
            processed.push({
                ...item,
                is_move_out_card: false,
                override_is_move_out: false,
                is_archived: false,
                percentage_completed: 100,
                is_completed: true,
                _card_key: `${item.openflow_id}_movein`,
            });

            // 2. Move Out Card
            const moveOutObj = item?.open_flow_move_out;
            const isMoveOutArchived = Boolean(item?.is_archived || moveOutObj?.is_archived);
            const moveOutPercentage = moveOutObj?.percentage_completed ?? item?.move_out_percentage ?? 0;
            const isMoveOutCompleted = moveOutObj?.move_out_status === 3 || moveOutPercentage === 100;

            processed.push({
                ...item,
                is_move_out_card: true,
                override_is_move_out: true,
                is_archived: isMoveOutArchived,
                percentage_completed: moveOutPercentage,
                is_completed: isMoveOutCompleted,
                _card_key: `${item.openflow_id}_moveout`,
            });
        } else {
            // Standard single card
            processed.push({
                ...item,
                is_move_out_card: Boolean(item?.is_move_out || (item?.move_out_status && item?.move_out_status > 0)),
                _card_key: `${item.openflow_id}`,
            });
        }
    });

    return processed;
};

const BoxItem = ({ restStyle, uri, onOpenflowClick, data }: any) => {
    const themeObj = getTokens('light').colors;
    const [showArchived, setShowArchived] = useState(false);

    const allProcessedData = getProcessedFlowData(data);

    const activeData = allProcessedData.filter(item => !item?.is_archived);
    const archivedData = allProcessedData.filter(item => item?.is_archived);

    const renderTile = (item: any) => {
        const isNha = isNhaAgreement(item);
        const address = getPropertyDisplayAddress(item);
        const negotiator = getNegotiatorName(item);
        const startDate = getStartDateFormatted(item);
        const branchName = getBranchName(item);
        const agreementLabel = getAgreementTypeLabel(item);
        const progressStatus = getTenancyProgressText(item);

        const isFlowCompleted = Boolean(item?.is_completed || item?.percentage_completed === 100);
        const completionPercentage = item?.percentage_completed ?? (isFlowCompleted ? 100 : 0);

        // Status badge flags: Renew if is_renewed, Move out if is_move_out, Else Move in
        const isRenewedState = Boolean(item?.is_renewed || item?.is_renew);
        const isMoveOutState = item?.override_is_move_out !== undefined
            ? item.override_is_move_out
            : Boolean(item?.is_move_out || (item?.move_out_status && item?.move_out_status > 0));

        const screenType = getScreenType();
        const isWeb = screenType === 'web';

        // Theme token constants
        const nhaGradient = themeObj?.main?.purple?.gradient || ['#9575CD', '#7E57C2'];
        const aptGradient = themeObj?.main?.blue?.gradient || ['#4C90AF', '#9ED6DC'];
        const headerColors = isNha ? nhaGradient : aptGradient;
        const badgeLabel = isNha ? 'NHA' : 'APT';

        const whiteColor = themeObj?.neutral?.white || '#FFFFFF';
        const primaryTextColor = themeObj?.text?.primary || '#2B3541';
        const secondaryTextColor = themeObj?.text?.secondary || '#3F4D5A';
        const successBg = themeObj?.feedback?.success?.[100] || '#D1F4EC';
        const successColor = themeObj?.feedback?.success?.[700] || '#00C897';
        const moveOutBtnBg = '#778df9';
        const iconColor = themeObj?.button?.main || '#1FA6B2';
        const borderColor = themeObj?.other?.['border-neutral'] || themeObj?.disabled?.button || '#E2E8F0';

        return (
            <TouchableOpacity
                activeOpacity={0.9}
                key={item?._card_key || item?.openflow_id}
                onPress={() => onOpenflowClick(item?.openflow_id, item)}
                style={[
                    tileStyles.cardContainer,
                    { borderColor },
                    isWeb
                        ? { width: 480, margin: 12 }
                        : { width: '100%', marginVertical: 8 }
                ]}
            >
                {/* ── Header with Platform Specific Gradient & Pill Badge ── */}
                <GradientWrapper
                    colors={headerColors}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={tileStyles.headerGradient}
                >
                    <View style={tileStyles.headerRow}>
                        <ODSText
                            type="sm-bold"
                            color={whiteColor}
                            numberOfLines={1}
                            style={tileStyles.addressText}
                        >
                            {address}
                        </ODSText>

                        <View style={tileStyles.pillBadge}>
                            <ODSText type="xs-bold" color={whiteColor} style={tileStyles.pillBadgeText}>
                                {badgeLabel}
                            </ODSText>
                        </View>
                    </View>
                </GradientWrapper>

                {/* ── Card Body Content ── */}
                <View style={tileStyles.cardBody}>
                    <View style={tileStyles.mainRow}>
                        {/* Info Column */}
                        <View style={tileStyles.infoCol}>
                            <ODSText type="xs" color={secondaryTextColor} numberOfLines={1}>
                                <ODSText type="xs-bold" color={primaryTextColor}>Negotiator: </ODSText>
                                {negotiator}
                            </ODSText>
                            <ODSText type="xs" color={secondaryTextColor} marginTop={6} numberOfLines={1}>
                                <ODSText type="xs-bold" color={primaryTextColor}>Start date: </ODSText>
                                {startDate}
                            </ODSText>
                        </View>

                        {/* Status Circle */}
                        <View style={tileStyles.centerCol}>
                            {isFlowCompleted ? (
                                <>
                                    <View style={[tileStyles.checkCircle, { backgroundColor: successBg }]}>
                                        <ODSIcon icon="TB-Check" size={18} color={successColor} />
                                    </View>
                                    <ODSText type="xs-bold" color={secondaryTextColor} marginTop={4}>
                                        Complete!
                                    </ODSText>
                                </>
                            ) : (
                                <>
                                    <View style={[tileStyles.checkCircle, { backgroundColor: '#FEF3C7' }]}>
                                        <ODSText type="xs-bold" color="#D97706" style={{ fontSize: 11 }}>
                                            {`${completionPercentage}%`}
                                        </ODSText>
                                    </View>
                                    <ODSText type="xs-bold" color={secondaryTextColor} marginTop={4}>
                                        In Progress
                                    </ODSText>
                                </>
                            )}
                        </View>

                        {/* Status Badge: Renew if is_renewed, Move out if is_move_out, Else Move in */}
                        <View style={tileStyles.badgesContainer}>
                            {isRenewedState ? (
                                <GradientWrapper
                                    colors={nhaGradient}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 0 }}
                                    style={tileStyles.statusPillGradient}
                                >
                                    <View style={tileStyles.statusPill}>
                                        <ODSIcon icon="TB-Renewals" size={12} color={whiteColor} />
                                        <ODSText type="xs-bold" color={whiteColor} style={{ marginLeft: 4 }}>
                                            Renew
                                        </ODSText>
                                    </View>
                                </GradientWrapper>
                            ) : isMoveOutState ? (
                                <View style={[tileStyles.statusPill, { backgroundColor: moveOutBtnBg }]}>
                                    <ODSText type="xs-bold" color={whiteColor}>
                                        Move out
                                    </ODSText>
                                </View>
                            ) : (
                                <GradientWrapper
                                    colors={aptGradient}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 0 }}
                                    style={tileStyles.statusPillGradient}
                                >
                                    <View style={tileStyles.statusPill}>
                                        <ODSIcon icon="TB-Move-In" size={12} color={whiteColor} />
                                        <ODSText type="xs-bold" color={whiteColor} style={{ marginLeft: 4 }}>
                                            Move in
                                        </ODSText>
                                    </View>
                                </GradientWrapper>
                            )}
                        </View>
                    </View>

                    {/* Divider */}
                    <View style={[tileStyles.divider, { backgroundColor: themeObj?.neutral?.[200] || '#F1F5F9' }]} />

                    {/* Footer Row */}
                    <View style={tileStyles.footerRow}>
                        <View style={tileStyles.footerItem}>
                            <ODSIcon icon="TB-User-Circle" size={13} color={iconColor} />
                            <View style={{ marginLeft: 5, flexShrink: 1 }}>
                                <ODSText type="xs-bold" color={secondaryTextColor} style={{ fontSize: 10 }}>
                                    Branch
                                </ODSText>
                                <ODSText type="xs" color={primaryTextColor} numberOfLines={1} style={{ fontSize: 10 }}>
                                    {branchName}
                                </ODSText>
                            </View>
                        </View>

                        <View style={tileStyles.footerItem}>
                            <ODSIcon icon="doc" size={13} color={iconColor} />
                            <View style={{ marginLeft: 5, flexShrink: 1 }}>
                                <ODSText type="xs-bold" color={secondaryTextColor} style={{ fontSize: 10 }}>
                                    Agreement
                                </ODSText>
                                <ODSText type="xs" color={primaryTextColor} numberOfLines={1} style={{ fontSize: 10 }}>
                                    {agreementLabel}
                                </ODSText>
                            </View>
                        </View>

                        <View style={tileStyles.footerItem}>
                            <ODSIcon icon="agrement" size={13} color={iconColor} />
                            <View style={{ marginLeft: 5, flexShrink: 1 }}>
                                <ODSText type="xs-bold" color={secondaryTextColor} style={{ fontSize: 10 }}>
                                    Status
                                </ODSText>
                                <ODSText type="xs" color={primaryTextColor} numberOfLines={1} style={{ fontSize: 10 }}>
                                    {progressStatus}
                                </ODSText>
                            </View>
                        </View>
                    </View>
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <View style={[styles(themeObj).containerStyle, restStyle]}>
            <View style={styles(themeObj).headerContainer}>
                <ODSText textAlign="center" color={themeObj?.text?.primary || 'black'} type="h4">
                    My Tenancies
                </ODSText>
            </View>

            {/* Active Tenancies Section */}
            <View style={getScreenType() !== 'phone' ? styles(themeObj).tilesContainerWeb : styles(themeObj).tilesContainerMobile}>
                {activeData?.length > 0 ? (
                    <FlatList
                        numColumns={getScreenType() === 'web' ? 2 : 1}
                        data={activeData}
                        nestedScrollEnabled={true}
                        contentContainerStyle={{ paddingRight: 5 }}
                        keyExtractor={(item) => (item?._card_key || item.openflow_id).toString()}
                        renderItem={({ item }) => renderTile(item)}
                    />
                ) : (
                    <ODSText type="md" color={themeObj.text.primary} textAlign="center">
                        No active tenancies found
                    </ODSText>
                )}
            </View>

            {/* Archive Toggle Button */}
            <TouchableOpacity
                style={styles(themeObj).archiveToggleContainer}
                onPress={() => setShowArchived(!showArchived)}
            >
                <View style={styles(themeObj).archiveToggle}>
                    <ODSText color={themeObj.button.main} type="h2-light">
                        {showArchived ? "-" : "+"}
                    </ODSText>
                    <ODSText
                        color={themeObj.text.primary}
                        type="md-bold"
                        marginTop={4}
                        marginLeft={16}
                    >
                        {showArchived ? "Hide Archived Tenancies" : "Show Archived Tenancies"}
                    </ODSText>
                </View>
            </TouchableOpacity>

            {/* Archived Tenancies Section */}
            {showArchived && (
                <View style={[
                    getScreenType() !== 'phone' ? styles(themeObj).tilesContainerWeb : styles(themeObj).tilesContainerMobile,
                    styles(themeObj).archivedSection
                ]}>
                    {archivedData?.length > 0 ? (
                        <FlatList
                            numColumns={getScreenType() === 'web' ? 2 : 1}
                            data={archivedData}
                            nestedScrollEnabled={true}
                            contentContainerStyle={{ paddingRight: 5 }}
                            keyExtractor={(item) => (item?._card_key || item.openflow_id).toString()}
                            renderItem={({ item }) => renderTile(item)}
                        />
                    ) : (
                        <ODSText type="md" color={themeObj.text.primary} textAlign="center">
                            No archived tenancies found
                        </ODSText>
                    )}
                </View>
            )}
        </View>
    );
};

const tileStyles = StyleSheet.create({
    cardContainer: {
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        overflow: 'hidden',
        shadowColor: '#101828',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
        elevation: 3,
    },
    headerGradient: {
        width: '100%',
        overflow: 'hidden',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        paddingVertical: 12,
        paddingHorizontal: 14,
    },
    addressText: {
        flex: 1,
        flexShrink: 1,
        marginRight: 8,
        letterSpacing: 0.3,
        fontSize: 12,
        lineHeight: 16,
    },
    pillBadge: {
        flexShrink: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.4)',
        borderRadius: 20,
        paddingHorizontal: 8,
        paddingVertical: 3,
        alignSelf: 'center',
    },
    pillBadgeText: {
        fontSize: 10,
        letterSpacing: 0.6,
    },
    cardBody: {
        padding: 16,
    },
    mainRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    infoCol: {
        flex: 1.2,
        marginRight: 8,
    },
    centerCol: {
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 8,
    },
    checkCircle: {
        width: 38,
        height: 38,
        borderRadius: 19,
        justifyContent: 'center',
        alignItems: 'center',
    },
    badgesContainer: {
        flexDirection: 'column',
        gap: 6,
        alignItems: 'flex-end',
        marginLeft: 8,
    },
    statusPillGradient: {
        borderRadius: 12,
        overflow: 'hidden',
    },
    statusPill: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 5,
        paddingHorizontal: 12,
        borderRadius: 12,
    },
    divider: {
        height: 1,
        marginVertical: 14,
    },
    footerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    footerItem: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        marginRight: 4,
    },
});

const styles = (theme: any) =>
    StyleSheet.create({
        containerStyle: {
            width: getScreenType() === 'web' ? 1060 : getScreenType() === 'phone' ? '100%' : '100%',
            maxWidth: '100%',
            borderRadius: 12,
            shadowColor: '#101828',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
            paddingVertical: getScreenType() === 'phone' ? 16 : 32,
            paddingHorizontal: getScreenType() === 'phone' ? 12 : 24,
            backgroundColor: theme.neutral['white'],
            borderWidth: 1,
            borderColor: theme.other?.['border-neutral'] || '#D0D6DD',
            alignSelf: 'center',
            marginBottom: 100,
            top: 0,
        },
        headerContainer: {
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 24,
            width: '100%',
        },
        archiveToggleContainer: {
            marginTop: 20,
            marginBottom: 10,
            alignItems: 'center',
            borderTopWidth: 1,
            borderTopColor: theme.other?.['border-neutral'] || '#E5E7EB',
            paddingTop: 20,
        },
        archiveToggle: {
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'flex-start',
        },
        tilesContainerWeb: {
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
        },
        tilesContainerMobile: {
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
        },
        archivedSection: {
            marginTop: 10,
            paddingTop: 10,
        },
    });

export default BoxItem;
